import React, {useContext, useState} from 'react';
import { useSelector } from 'react-redux';
import useStyles from 'isomorphic-style-loader/useStyles';
import PropTypes from 'prop-types';
import { OutlinedInput, IconButton } from '@material-ui/core';
import EditIcon from '@material-ui/icons/Edit';
import CheckIcon from '@material-ui/icons/Check';
import CloseIcon from '@material-ui/icons/Close';

// Material UI
import Avatar from '@material-ui/core/Avatar';

// app modules: context
import { CommentsContext } from 'components/Comments';

// app modules: services
import assets from 'services/assets';

// assets
import CssDisplayNameChooser from './DisplayNameChooser.scss';

const DisplayNameChooser = props => {
  const [newName, setNewName] = useState('');
  const { label, showAvatar, canEditName, onNameSubmit, isEditingName, setIsEditingName, disabledNameField } = props;
  useStyles(CssDisplayNameChooser);
  const { asAuthor } = useContext(CommentsContext);
  const name = useSelector(state => state.user.display_name);
  const avatar = useSelector(state => state.user.avatar);

  const toggleIsEditing = editingState => {
    if (!setIsEditingName) {
      return;
    }

    if (typeof editingState === 'boolean') {
      setIsEditingName(editingState);

      return;
    }

    setIsEditingName(prevState => !prevState);
  };

  const handleNameInput = ev => {
    ev.preventDefault();
    setNewName(ev.target.value);
  };

  const handleNameSubmit = () => {
    if (!onNameSubmit) {
      return;
    }

    onNameSubmit(newName);
  };

  return (
    <div className="display-name-chooser">
      {showAvatar ? (
        <Avatar
          src={assets.avatarUrl(avatar || 'default', {
            resize: 'fit-in',
            width: 80,
            height: 80,
          })}
        />
      ) : null}
      <div className="display-name-chooser__name">
        {isEditingName ? (
          <>
            <OutlinedInput
              inputRef={input => input && input.focus()}
              defaultValue={name}
              disabled={disabledNameField}
              onChange={handleNameInput}
              label="Display name"
            />
            <IconButton
              onClick={handleNameSubmit}
              type="button"
              disabled={!newName || name === newName || disabledNameField}
              className="display-name-chooser__name__button"
            >
              <CheckIcon className="display-name-chooser__name__button__icon" />
            </IconButton>
          </>
        ) : (
          <p onClick={toggleIsEditing}>
            {label ? <span>{label}: </span> : null}
            <strong>{name}</strong>
          </p>
        )}
        {canEditName && (
          <IconButton
            type="button"
            onClick={toggleIsEditing}
            className="display-name-chooser__name__button"
          >
            {isEditingName ? (
              <CloseIcon color="error" />
            ) : (
              <EditIcon fontSize="small" color="primary" />
            )}
          </IconButton>
        )}
      </div>
      {asAuthor ? <div className="display-name-chooser__badge">Author</div> : null}
    </div>
  );
};

DisplayNameChooser.propTypes = {
  label: PropTypes.string,
  showAvatar: PropTypes.bool,
  canEditName: PropTypes.bool,
  onNameSubmit: PropTypes.func,
  isEditingName: PropTypes.bool,
  disabledNameField: PropTypes.bool,
  setIsEditingName: PropTypes.func,
};

DisplayNameChooser.defaultProps = {
  label: null,
  showAvatar: true,
  canEditName: false,
  disabledNameField: false,
  onNameSubmit: () => {},
  isEditingName: false,
  setIsEditingName: () => {},
};

export default React.memo(DisplayNameChooser);
